import {
    AdUnitWrapper,
    createIsEntitledSubscribeChangeHandler,
    featurePublicationPageType,
    FeatureToggle,
    FlyoutNav,
    LazyTheWestLiveStickyBanner,
    MastHeadLogo,
    QuickLinks,
    StyledFooterLogo,
    StyledStickyFooterAdUnit,
    TermsFooter,
    TheWestFooterNetworkLinks,
    TheWestHeader,
    TheWestHeaderActions,
    TheWestStickySiteHeader,
    useSectionMetaInfo,
} from '@news-mono/component-library'
import {
    AppState,
    ConfigurationContext,
    EntitledToPremium,
    getBaseUrl,
    isPuzzlePage,
    RenderTargetContext,
    RequiredAccess,
} from '@news-mono/web-common'
import { PageProps } from 'page-lifecycle-provider'
import React, { useContext } from 'react'
import { useSelector } from 'react-redux'
import version from '../../server/version'
import { getFooterLinks } from '../footer'
import { getStaticNavInHouseLinks, navigation, quickLinks } from '../navigation'
import { publicationPageType } from '../routes/publication/kind/article'
import { BaseTheWestProps } from './BaseTheWest'
import {
    StyledBaseGrid,
    StyledCell,
    StyledFooterCell,
    StyledNestedCell,
    StyledNestedGrid,
} from './BaseTheWest.styled'

export interface Props {
    headerAd?: (rendered?: () => void) => JSX.Element | undefined
    search?: JSX.Element
    header: JSX.Element
    primaryNav: JSX.Element
    flyoutNav: JSX.Element
    content: JSX.Element
    footerNav?: JSX.Element
    footerNetworkLinks: JSX.Element
    footerTermsLinks: JSX.Element
    footerAd?: JSX.Element | false
    disableStickyFooterAd?: boolean
    pageType: string
    // DPT-1375 extra Metadata for variant testing. Remove when finished
    pageProperties?: {
        requiredAccess: RequiredAccess
        publicationKind: 'article' | 'redirect' | 'event'
        layout?: 'default' | 'feature'
    }
}

export const BaseTheWestWebTemplate: React.FC<Props> = ({
    disableStickyFooterAd = false,
    pageProperties,
    ...props
}) => {
    return (
        <StyledBaseGrid>
            <StyledCell>
                {props.flyoutNav}
                <TheWestStickySiteHeader
                    headerAd={props.headerAd && props.headerAd}
                    header={props.header}
                    navigation={props.primaryNav}
                    disableSticky={pageProperties?.publicationKind === 'event'}
                />
                <StyledCell hasBackground={false}>{props.content}</StyledCell>
            </StyledCell>
            <StyledCell>{props.footerNav}</StyledCell>
            <StyledCell hasBackground={true}>
                <StyledNestedGrid>
                    <StyledNestedCell>
                        {props.footerNetworkLinks}
                    </StyledNestedCell>
                </StyledNestedGrid>
            </StyledCell>
            <StyledFooterCell disableStickyFooterAd={disableStickyFooterAd}>
                <StyledNestedGrid>
                    <StyledNestedCell>
                        {props.footerTermsLinks}
                    </StyledNestedCell>
                    {props.footerAd && (
                        <StyledNestedCell>{props.footerAd}</StyledNestedCell>
                    )}
                </StyledNestedGrid>
            </StyledFooterCell>
        </StyledBaseGrid>
    )
}
BaseTheWestWebTemplate.displayName = 'BaseTheWestWebTemplate'

export const BaseTheWestWeb: React.FC<BaseTheWestProps> = ({
    additionalPageProperties,
    ads,
    onEvent,
    config,
    pageType,
    pageContents,
    section,
    flyOutProps,
    location,
}) => {
    /**
     * DPT-955 - Dont render quicklinks on feature publication page as we need to render it with a fill
     * so we will do that inside the routing for feature publication with quicklinks.routing.
     * DPT-1338 - we also don't want to render it on regional pages
     */

    const isArticlePage =
        pageType === featurePublicationPageType ||
        pageType === publicationPageType

    const isEventPage = additionalPageProperties.publicationKind === 'event'

    const showQuickLinks = !isArticlePage && section === 'default'

    const sectionMeta = useSectionMetaInfo()
    const footerLinks = getFooterLinks(config, sectionMeta)
    const { publicUrl } = useContext(ConfigurationContext)
    const baseUrl = getBaseUrl(publicUrl, sectionMeta)

    const renderTargetContext = React.useContext(RenderTargetContext)
    const renditionType = useSelector(
        (state: AppState) => state.render.renditionType,
    )

    const isAppRenditionPuzzlePage =
        renditionType === 'app' && isPuzzlePage({ location })

    const isRegionalSwgCampaignPage = pageType === 'regional-swg-campaign'

    return (
        <React.Fragment>
            <EntitledToPremium
                // Used to track subscribe.complete events across the site, this differs from breachscreen.complete
                onIsEntitledChange={createIsEntitledSubscribeChangeHandler(
                    location,
                    onEvent,
                )}
            />
            <FeatureToggle
                feature="the-west-live"
                on={() => {
                    if (
                        !config.theWestLiveStationId ||
                        renditionType === 'app'
                    ) {
                        return null
                    }
                    return (
                        <LazyTheWestLiveStickyBanner
                            showSponsorLogo={true}
                            bannerAd={(rendered) =>
                                ads && (
                                    <AdUnitWrapper
                                        noticePosition="none"
                                        padding={[4, 0, 0, 0]}
                                        adState={ads}
                                        unitId="twl-player"
                                        unitRendered={rendered}
                                        onEvent={onEvent}
                                    />
                                )
                            }
                        />
                    )
                }}
            />
            <BaseTheWestWebTemplate
                // DPT-1375 extra Metadata for variant testing. Remove when finished
                pageProperties={additionalPageProperties as any}
                pageType={pageType}
                disableStickyFooterAd={isAppRenditionPuzzlePage}
                headerAd={(rendered) =>
                    ads && (
                        <AdUnitWrapper
                            noticePosition="above-center"
                            padding={[4, 0, 0, 0]}
                            adState={ads}
                            unitId="header"
                            unitRendered={rendered}
                            onEvent={onEvent}
                        />
                    )
                }
                header={
                    <TheWestHeader
                        inHouseLinks={getStaticNavInHouseLinks(
                            baseUrl,
                            section,
                        )}
                        pageType={pageType}
                        onEvent={onEvent}
                        isRegionalSwgCampaignPage={isRegionalSwgCampaignPage}
                    />
                }
                flyoutNav={
                    <FlyoutNav
                        navLinks={navigation(
                            baseUrl,
                            renderTargetContext.renderTarget,
                        )}
                        section={section}
                        mainNavOpen={flyOutProps.mainNavOpen}
                        closeNavigation={flyOutProps.closeNavigation}
                        onEvent={onEvent}
                    />
                }
                primaryNav={
                    isAppRenditionPuzzlePage ? (
                        <></>
                    ) : (
                        <TheWestHeaderActions
                            mainNavOpen={flyOutProps.mainNavOpen}
                            openNavigation={flyOutProps.openNavigation}
                            closeNavigation={flyOutProps.closeNavigation}
                            onEvent={onEvent}
                            hideBurger={isPuzzlePage({ location })}
                        />
                    )
                }
                content={
                    <React.Fragment>
                        {showQuickLinks && (
                            <QuickLinks links={quickLinks} onEvent={onEvent} />
                        )}
                        {pageContents}
                        <PageProps
                            pageProperties={{
                                ...additionalPageProperties,
                                section,
                                pageType,
                                codebase: {
                                    version: version.buildNumber,
                                    sha: version.sha,
                                },
                            }}
                        ></PageProps>
                    </React.Fragment>
                }
                footerNetworkLinks={
                    <TheWestFooterNetworkLinks
                        networkLinks={footerLinks.networkLinks}
                        socialLinks={footerLinks.socialLinks}
                    />
                }
                footerTermsLinks={
                    <TermsFooter
                        footerCompanyLinks={footerLinks.footerCompanyLinks}
                        footerTermsLinks={footerLinks.footerTermsLinks}
                        additionalTerms={
                            <StyledFooterLogo>
                                <MastHeadLogo isFooterLogo={true} />
                            </StyledFooterLogo>
                        }
                    />
                }
                footerAd={
                    ads &&
                    !isAppRenditionPuzzlePage && (
                        <StyledStickyFooterAdUnit>
                            <AdUnitWrapper
                                noticePosition="none"
                                padding={[4, 0]}
                                adState={ads}
                                unitId="sticky-footer"
                                onEvent={onEvent}
                            />
                        </StyledStickyFooterAdUnit>
                    )
                }
            />
        </React.Fragment>
    )
}
BaseTheWestWeb.displayName = 'BaseTheWestWeb'
